/** Star-system-centric data model for the galactic spatial map */

export interface StarSystem {
	id: string;
	name: string;
	star: StarData;
	planets: PlanetData[];
	x: number; // spatial X in light-years from Sol
	y: number; // spatial Y in light-years from Sol
	zone: number; // 0-29, computed from distance to Sol
	systemIndex: number; // on-chain index (0 = Sol, 1-31 = known, 32+ = procedural)
}

export interface StarData {
	name: string;
	spectralClass: string;
	apparentMagnitude: number;
	absoluteMagnitude: number;
	distanceLightYears: number;
	stellarTemp?: number | null; // Kelvin
	stellarMass?: number | null; // Solar masses
	stellarRadius?: number | null; // Solar radii
}

export interface PlanetData {
	id: string;
	name: string;
	orbitalRadius: number; // AU from host star
	orbitalPeriod: number; // days
	radius: number; // Earth radii
	mass?: number | null; // Earth masses
	discoveryMethod?: string;
	color: string;
	equilibriumTemp?: number | null; // Kelvin
	eccentricity?: number | null;
}

export type CelestialObject =
	| { type: "star"; data: StarSystem }
	| { type: "planet"; data: PlanetData; system: StarSystem };

export interface ViewState {
	offsetX: number;
	offsetY: number;
	scale: number;
}

// Convert equatorial RA/DEC + distance to spatial XY (light-years from Sol)
// RA in hours (0-24), DEC in degrees (-90 to +90), distance in light-years
export function celestialToSpatial(
	ra: number,
	dec: number,
	distance: number,
): { x: number; y: number } {
	const raRad = (ra * Math.PI) / 12;
	const decRad = (dec * Math.PI) / 180;
	const x = distance * Math.cos(decRad) * Math.cos(raRad);
	const y = distance * Math.cos(decRad) * Math.sin(raRad);
	return { x, y };
}

// Get star color based on spectral class
export function getStarColor(spectralClass: string): string {
	const type = spectralClass.charAt(0).toUpperCase();
	const colors: Record<string, string> = {
		O: "#9bb0ff",
		B: "#aabfff",
		A: "#cad7ff",
		F: "#f8f7ff",
		G: "#fff4ea",
		K: "#ffd2a1",
		M: "#ffcc6f",
	};
	return colors[type] ?? "#ffffff";
}

// Get star display size based on magnitude (brighter = larger)
export function getStarSize(magnitude: number): number {
	const normalized = Math.max(0, Math.min(7, magnitude + 1)) / 7;
	return 3 + (1 - normalized) * 12;
}

/** View mode for discrete navigation */
export type ViewMode = "galaxy" | "system";

/** Star dot size for galaxy view: 2–6px based on apparent magnitude */
export function getGalaxyDotSize(apparentMagnitude: number): number {
	const normalized = Math.max(0, Math.min(1, (apparentMagnitude + 2) / 20));
	return 6 - normalized * 4;
}

/** Star display size in system view (centered glow): 20–50px */
export function getSystemStarSize(absoluteMagnitude: number): number {
	const normalized = Math.max(0, Math.min(1, (absoluteMagnitude + 8) / 25));
	return 20 + (1 - normalized) * 30;
}
